import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';

export type ToastKind = 'info' | 'success' | 'error' | 'warn';

export interface Toast {
  readonly id: number;
  readonly title: string;
  readonly message: string;
  readonly kind: ToastKind;
  /** True for the last 300 ms of the toast's life, while it fades out. */
  readonly leaving: boolean;
}

/** Four is the ceiling: a fifth simultaneous toast is noise, not information. */
export const TOAST_LIMIT = 4;
/** Matches the mockup's dwell time. */
export const TOAST_TTL_MS = 4200;
/** Matches the .3s fade in toast-host.scss. Keep the two in step. */
export const TOAST_FADE_MS = 300;

/**
 * Transient notifications. A plain signal array - no RxJS, because there is no
 * stream to compose and the app is zoneless, so the timers must write to a
 * signal or nothing repaints.
 */
@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly items = signal<readonly Toast[]>([]);
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>[]>();
  private sequence = 0;

  readonly toasts = this.items.asReadonly();
  readonly count = computed(() => this.items().length);

  constructor() {
    inject(DestroyRef).onDestroy(() => this.clear());
  }

  /** Returns the id, so a caller can dismiss early. */
  show(title: string, message: string, kind: ToastKind = 'info'): number {
    const id = ++this.sequence;
    const toast: Toast = { id, title, message, kind, leaving: false };

    this.items.update((current) => {
      // Drop the oldest rather than refusing the newest: the most recent
      // message is the one the user is waiting on.
      const kept = current.length >= TOAST_LIMIT ? current.slice(current.length - TOAST_LIMIT + 1) : current;
      for (const dropped of current.slice(0, current.length - kept.length)) {
        this.clearTimers(dropped.id);
      }
      return [...kept, toast];
    });

    this.timers.set(id, [
      setTimeout(() => this.fade(id), TOAST_TTL_MS),
      setTimeout(() => this.dismiss(id), TOAST_TTL_MS + TOAST_FADE_MS),
    ]);
    return id;
  }

  success(title: string, message: string): number {
    return this.show(title, message, 'success');
  }

  error(title: string, message: string): number {
    return this.show(title, message, 'error');
  }

  warn(title: string, message: string): number {
    return this.show(title, message, 'warn');
  }

  dismiss(id: number): void {
    this.clearTimers(id);
    this.items.update((current) => current.filter((toast) => toast.id !== id));
  }

  clear(): void {
    for (const id of [...this.timers.keys()]) this.clearTimers(id);
    this.items.set([]);
  }

  private fade(id: number): void {
    this.items.update((current) =>
      current.map((toast) => (toast.id === id ? { ...toast, leaving: true } : toast))
    );
  }

  private clearTimers(id: number): void {
    for (const handle of this.timers.get(id) ?? []) clearTimeout(handle);
    this.timers.delete(id);
  }
}
